import React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type {
  SubscriptionCar,
  SubscriptionModel,
} from '@/api/subscriptions/types';
import { useSubscriptionOptions } from '@/api/subscriptions/use-subscription-options';
import {
  camelCaseToSnakeCase,
  camelCaseToTitleCase,
  capitalizeFirstLetter,
  formatNumber,
  getSubscriptionBackgroundImageUri,
  snakeToCamelCase,
  snakeToTitleCase,
  useCurrentOrganization,
} from '@/core';
import useSubscriptionStore from '@/stores/use-subscription-store';
import { PINKLING } from '@/style/colors';
import { Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { ModelCardItem } from '@/ui/card-item/model-card-item';
import { ModelTypeCardItem } from '@/ui/card-item/model-type-card-item';
import { Eternity } from '@/ui/icons/eternity';
import { Loader } from '@/ui/loader';

export default function ChooseCar() {
  const { styles, theme } = useStyles(stylesheet);
  const period = useSubscriptionStore((state) => state.period);
  const setBrandName = useSubscriptionStore((state) => state.setBrandName);
  const setModelName = useSubscriptionStore((state) => state.setModelName);
  const setTrimName = useSubscriptionStore((state) => state.setTrimName);
  const setCar = useSubscriptionStore((state) => state.setCar);
  const brandName = useSubscriptionStore((state) => state.brandName);
  const modelName = useSubscriptionStore((state) => state.modelName);
  const trimName = useSubscriptionStore((state) => state.trimName);
  const address = useSubscriptionStore((state) => state.address);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const { isEVFMC } = useCurrentOrganization();

  const { data: subscriptionOptions, isLoading } = useSubscriptionOptions({
    variables: {
      address: address?.description,
      startDate: startDatetime!,
    },
    enabled: !!address && !!startDatetime,
  });

  const getSubtitle = (modelName: string) => {
    switch (modelName) {
      case 'model_3':
        return 'Compact sedan with great range.';
      case 'model_y':
        return 'Versatile crossover with ample space.';
      case 'model_x':
        return 'Spacious SUV with Falcon Wing doors.';
      case 'model_s':
        return 'Luxury sedan with extended range.';
      case 'cybertruck':
        return 'Futuristic truck with bold design.';
      case 'r1s':
        return 'Rugged three-row SUV.';
      case 'r1t':
        return 'Adventure-ready electric pickup.';
      case 'air':
        return 'Lucid Air: Ultra-luxury sedan with record range.';
      default:
        return '';
    }
  };

  const renderPostscript = (car: SubscriptionCar) => {
    if (trimName !== snakeToCamelCase(car.trim.name)) return;

    if (period === 'weekly') {
      return (
        <View style={{ flexDirection: 'row', gap: 4 }}>
          <Text style={{ color: PINKLING }} type="caption1">
            Nearest Location:
          </Text>

          <Text type="caption1" style={{ flex: 1 }}>
            {`${car.defaultLocation.address} ${
              car.defaultLocation.city
            }, ${snakeToTitleCase(car.defaultLocation.locationState)}, ${
              car.defaultLocation.zipcode
            }`}
          </Text>
        </View>
      );
    }

    if (car.deliveryCost > 0) {
      return (
        <View style={{ flexDirection: 'row', gap: 4 }}>
          <Text style={{ color: theme.colors.error, flex: 1 }} type="caption1">
            Free delivery is not available for this model. One-time delivery fee
            applies.
          </Text>
        </View>
      );
    }

    return null;
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <View style={styles.container}>
      <View style={{ gap: 16 }}>
        <View>
          <Text tx="subscriptions.chooseCarModel" type="bodyBold" />

          {!isEVFMC ? (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Choose how often you'd like your subscription to renew - the
              longer the subscription, the more you save.
            </Text>
          ) : null}
        </View>

        {isEVFMC ? (
          <Alert
            type={AlertType.BRAND}
            icon={<Eternity />}
            title="Unlimited mileage, always included."
            body="Every subscription comes with unlimited miles. No caps, no overage fees, ever."
            iconWidth={32}
            iconHeight={32}
            iconContainerStyle={{ alignSelf: 'center' }}
          />
        ) : null}

        <View style={styles.modelCardItemsContainer}>
          {subscriptionOptions &&
            subscriptionOptions[period] &&
            Object.keys(subscriptionOptions[period]).map(
              (key: SubscriptionModel) => {
                const value = subscriptionOptions[period][key];

                if (typeof value !== 'object') {
                  return;
                }

                const cheapestOption = subscriptionOptions[period][key]
                  ?.cheapestOption as SubscriptionCar;

                const splitKeys = camelCaseToSnakeCase(key).split('_');
                const brand = splitKeys[0];
                const model = splitKeys.splice(1).join('_');

                if (cheapestOption === undefined) {
                  return null;
                }

                return (
                  <ModelCardItem
                    key={key}
                    imageSrc={getSubscriptionBackgroundImageUri(brand, model)}
                    title={camelCaseToTitleCase(key)}
                    subtitle={getSubtitle(model)}
                    cost={`$${
                      cheapestOption
                        ? formatNumber(
                            period === '6Months'
                              ? cheapestOption?.cost / 6
                              : cheapestOption?.cost
                          )
                        : '-'
                    }`}
                    unit={subscriptionOptions[period]?.unit}
                    active={
                      modelName ===
                      capitalizeFirstLetter(
                        snakeToCamelCase(cheapestOption?.model?.name)
                      )
                    }
                    disabled={cheapestOption === undefined}
                    hideInitiationFee={isEVFMC}
                    onPress={() => {
                      setBrandName(cheapestOption.brand.name);
                      setModelName(
                        capitalizeFirstLetter(
                          snakeToCamelCase(cheapestOption.model.name)
                        )
                      );
                      setCar(null);
                      setTrimName('');
                    }}
                    style={styles.modelCardItem}
                  />
                );
              }
            )}
        </View>
      </View>

      {modelName && (
        <>
          <View style={{ gap: 16 }}>
            <View style={{ gap: 4 }}>
              <Text tx="subscriptions.chooseCarTrim" type="bodyBold" />
              <Text
                tx="subscriptions.chooseCarTrimBody"
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              />
            </View>

            <View style={styles.modelTypeCardItemsContainer}>
              {brandName &&
                modelName &&
                subscriptionOptions?.[period]?.[`${brandName}${modelName}`]
                  ?.trimChoices &&
                Object.keys(
                  subscriptionOptions[period][`${brandName}${modelName}`]
                    .trimChoices
                )
                  .sort((a, b) => {
                    const carsA = subscriptionOptions[period][
                      `${brandName}${modelName}`
                    ].trimChoices[a] as SubscriptionCar[];
                    const carsB = subscriptionOptions[period][
                      `${brandName}${modelName}`
                    ].trimChoices[b] as SubscriptionCar[];

                    return carsA[0].cost - carsB[0].cost;
                  })
                  .map((key: any) => {
                    const cars = subscriptionOptions[period][
                      `${brandName}${modelName}`
                    ].trimChoices[key] as SubscriptionCar[];

                    const cheapestOption = subscriptionOptions[period][
                      `${brandName}${modelName}`
                    ].cheapestOption as SubscriptionCar;

                    const car = cars[0];

                    const additionalCost =
                      period === '6Months'
                        ? (car.cost - cheapestOption.cost) / 6
                        : car.cost - cheapestOption.cost;

                    return (
                      <ModelTypeCardItem
                        key={key}
                        title={car.trim.displayName}
                        body={`${car.trim.batteryRange} miles of range`}
                        cost={
                          additionalCost > 0
                            ? `$${formatNumber(additionalCost)}`
                            : 'Included'
                        }
                        unit={
                          additionalCost > 0
                            ? `/ ${subscriptionOptions[period]?.unit}`
                            : ''
                        }
                        active={trimName === snakeToCamelCase(car.trim.name)}
                        onPress={() => {
                          setTrimName(snakeToCamelCase(car.trim.name));
                          setCar(car);
                        }}
                        style={styles.modelTypeCardItem}
                      />
                    );
                  })}
            </View>
          </View>
        </>
      )}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    paddingTop: 8,
    gap: 24,
    flexDirection: 'column',
  },
  modelCardItemsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  modelCardItem: {
    flexBasis: {
      xs: '100%',
      md: '32%',
    },
    maxWidth: {
      xs: '100%',
      md: '32%',
    },
    flexGrow: 1,
  },
  modelTypeCardItemsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  modelTypeCardItem: {
    flexBasis: {
      xs: '100%',
      md: '49%',
    },
    maxWidth: {
      xs: '100%',
      md: '49%',
    },
    flexGrow: 1,
  },
}));
